import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Car, User, ExternalLink, Trash2, Save, Edit3 } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import WarrantyStatusBadge from "../components/WarrantyStatusBadge.jsx";
import { fmtDate } from "../lib/format.js";
import { useAuth } from "../lib/auth.jsx";
import StatusBadge from "../components/StatusBadge.jsx";

const TABS = [
  { key: "summary", label: "Resumo" },
  { key: "soms", label: "Histórico de SOMs" },
  { key: "audit", label: "Registo de auditoria" },
];

export default function VehicleRegistrationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vr, setVr] = useState(null);
  const [soms, setSoms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("summary");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [enums, setEnums] = useState(null);

  const load = async () => {
    const [{ data: v }, { data: s }, { data: a }, { data: e }] = await Promise.all([
      api.get(`/vehicle-registrations/${id}`),
      api.get(`/vehicle-registrations/${id}/service-cases`),
      api.get(`/audit-log?record_type=vehicle_registration&record_id=${id}&limit=100`),
      api.get(`/enums`),
    ]);
    setVr(v);
    setSoms(s);
    setAudit(a);
    setEnums(e);
    setForm({
      registration_plate: v.registration_plate,
      brand: v.brand,
      model: v.model,
      colour: v.colour,
      year: v.year,
      engine_capacity: v.engine_capacity || "",
      transmission: v.transmission || "",
      fuel_type: v.fuel_type || "",
      warranty_start_date: (v.warranty_start_date || "").slice(0, 10),
      warranty_duration_months: v.warranty_duration_months,
      warranty_end_date: (v.warranty_end_date || "").slice(0, 10),
      sale_date: (v.sale_date || "").slice(0, 10),
      branch: v.branch,
      notes: v.notes || "",
      alidata_sale_reference: v.alidata_sale_reference || "",
    });
  };

  useEffect(() => { load(); }, [id]);

  if (!vr) return <div className="p-8 text-sm text-[#6C757D]">A carregar…</div>;

  const canEdit = ["admin", "national_aftersales_manager", "department_manager"].includes(user?.role)
    || (user?.effective_modules || []).includes("vehicle_registrations");

  const save = async () => {
    setError("");
    try {
      const { data } = await api.patch(`/vehicle-registrations/${id}`, {
        ...form,
        year: parseInt(form.year, 10),
        warranty_duration_months: parseInt(form.warranty_duration_months, 10),
        warranty_end_date: form.warranty_end_date || null,
        engine_capacity: form.engine_capacity || null,
        transmission: form.transmission || null,
        fuel_type: form.fuel_type || null,
        notes: form.notes || null,
        alidata_sale_reference: form.alidata_sale_reference || null,
        comment: "Registo actualizado",
      });
      setVr(data);
      setEditing(false);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    }
  };

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm" data-testid="vr-sticky-header">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/vehicle-registrations" className="flex items-center gap-1 hover:text-[#1F3864]">
            <ChevronLeft className="w-3 h-3" /> Registo de Viaturas
          </Link>
          <span>/</span>
          <span className="font-mono">{vr.vr_number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <Car className="w-5 h-5 text-[#1F3864]" />
              <h1 className="font-heading text-xl font-bold text-[#1A1A2E]" data-testid="vr-title">
                {vr.registration_plate} · {vr.brand} {vr.model}
              </h1>
              <WarrantyStatusBadge status={vr.warranty_status} daysToExpiry={vr.warranty_days_to_expiry} size="lg" />
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span className="font-mono">VIN {vr.vin}</span>
              <span>{vr.year} · {vr.colour}</span>
              <span>{vr.branch}</span>
              {vr.customer && (
                <Link to={`/customers/${vr.customer.id}`} className="text-[#1F3864] font-medium hover:underline flex items-center gap-1" data-testid="vr-customer-link">
                  <User className="w-3 h-3" /> {vr.customer.name} · {vr.customer.customer_number}
                </Link>
              )}
              {vr.salesperson_name && <span>Vendedor: {vr.salesperson_name}</span>}
            </div>
          </div>
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} className="text-sm border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-1.5 flex items-center gap-2" data-testid="vr-edit-btn">
              <Edit3 className="w-3.5 h-3.5" /> Editar
            </button>
          )}
        </div>

        <div className="mt-3 flex gap-1 border-b -mb-3">
          {TABS.map((t) => {
            const label = t.key === "soms" && soms.length > 0 ? `${t.label} (${soms.length})` : t.label;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`vr-tab-${t.key}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1F3864]"}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {tab === "summary" && (
          <div className="bg-white border border-gray-200 rounded-md p-5">
            {error && <div className="mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{error}</div>}
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <FieldEdit label="Matrícula" mono value={form.registration_plate} onChange={(v) => change("registration_plate", v.toUpperCase())} />
                <FieldEdit label="Marca" select options={enums?.brands || []} value={form.brand} onChange={(v) => change("brand", v)} />
                <FieldEdit label="Modelo" value={form.model} onChange={(v) => change("model", v)} />
                <FieldEdit label="Cor" value={form.colour} onChange={(v) => change("colour", v)} />
                <FieldEdit label="Ano" type="number" value={form.year} onChange={(v) => change("year", v)} />
                <FieldEdit label="Cilindrada" value={form.engine_capacity} onChange={(v) => change("engine_capacity", v)} />
                <FieldEdit label="Transmissão" select options={["", "Manual", "Automático"]} value={form.transmission} onChange={(v) => change("transmission", v)} />
                <FieldEdit label="Combustível" select options={["", "Gasolina", "Diesel", "Eléctrico", "Híbrido"]} value={form.fuel_type} onChange={(v) => change("fuel_type", v)} />
                <FieldEdit label="Data da venda" type="date" value={form.sale_date} onChange={(v) => change("sale_date", v)} />
                <FieldEdit label="Início garantia" type="date" value={form.warranty_start_date} onChange={(v) => change("warranty_start_date", v)} />
                <FieldEdit label="Duração garantia (meses)" type="number" value={form.warranty_duration_months} onChange={(v) => change("warranty_duration_months", v)} />
                <FieldEdit label="Fim garantia" type="date" value={form.warranty_end_date} onChange={(v) => change("warranty_end_date", v)} />
                <FieldEdit label="Dealer" select options={enums?.branches || []} value={form.branch} onChange={(v) => change("branch", v)} />
                <FieldEdit label="Ref. venda Alidata" value={form.alidata_sale_reference} onChange={(v) => change("alidata_sale_reference", v)} />
                <div className="col-span-2">
                  <FieldEdit label="Notas" textarea value={form.notes} onChange={(v) => change("notes", v)} />
                </div>
                <div className="col-span-2 flex justify-end gap-2 mt-2">
                  <button onClick={() => { setEditing(false); setError(""); }} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancelar</button>
                  <button onClick={save} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm flex items-center gap-2" data-testid="vr-save-btn">
                    <Save className="w-3.5 h-3.5" /> Guardar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Row label="Matrícula"><span className="font-mono text-[#1F3864] font-semibold">{vr.registration_plate}</span></Row>
                <Row label="VIN"><span className="font-mono">{vr.vin}</span></Row>
                <Row label="Marca / Modelo">{vr.brand} {vr.model}</Row>
                <Row label="Ano · Cor">{vr.year} · {vr.colour}</Row>
                <Row label="Cilindrada">{vr.engine_capacity || "—"}</Row>
                <Row label="Transmissão">{vr.transmission || "—"}</Row>
                <Row label="Combustível">{vr.fuel_type || "—"}</Row>
                <Row label="Data da venda">{fmtDate(vr.sale_date)}</Row>
                <Row label="Início garantia">{fmtDate(vr.warranty_start_date)}</Row>
                <Row label="Fim garantia">{fmtDate(vr.warranty_end_date)}</Row>
                <Row label="Duração garantia">{vr.warranty_duration_months} meses</Row>
                <Row label="Estado da garantia"><WarrantyStatusBadge status={vr.warranty_status} daysToExpiry={vr.warranty_days_to_expiry} /></Row>
                <Row label="Dealer">{vr.branch}</Row>
                <Row label="Vendedor">{vr.salesperson_name || "—"}</Row>
                <Row label="Ref. venda Alidata">{vr.alidata_sale_reference || "—"}</Row>
                <Row label="Cliente">
                  {vr.customer ? (
                    <Link to={`/customers/${vr.customer.id}`} className="text-[#1F3864] hover:underline">
                      {vr.customer.customer_number} · {vr.customer.name}
                    </Link>
                  ) : "—"}
                </Row>
                <div className="col-span-2">
                  <Row label="Notas">{vr.notes || "—"}</Row>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "soms" && (
          <div className="bg-white border border-gray-200 rounded-md">
            {soms.length === 0 ? (
              <div className="p-8 text-center text-[#6C757D] text-sm" data-testid="vr-soms-empty">Sem obras de manutenção para esta viatura.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
                  <tr><Th>SOM</Th><Th>Data</Th><Th>Estado</Th><Th>Service Advisor</Th><Th>Dealer</Th><Th>Queixa</Th></tr>
                </thead>
                <tbody>
                  {soms.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer" onClick={() => navigate(`/service-cases/${s.id}`)} data-testid={`vr-som-row-${s.id}`}>
                      <Td mono className="text-[#1F3864] font-semibold">{s.number}</Td>
                      <Td>{fmtDate(s.opened_at)}</Td>
                      <Td><StatusBadge status={s.current_status} /></Td>
                      <Td>{s.service_advisor_id ? "—" : "—"}</Td>
                      <Td>{s.branch}</Td>
                      <Td className="text-[#6C757D] max-w-md truncate">{s.customer_complaint}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "audit" && (
          <div className="bg-white border border-gray-200 rounded-md p-4 text-sm">
            {audit.length === 0 ? (
              <div className="text-[#6C757D]">Sem entradas de auditoria.</div>
            ) : audit.map((a) => (
              <div key={a.id} className="border-b border-gray-100 py-2 last:border-0" data-testid={`vr-audit-${a.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-[#1A1A2E]">{a.action}</span>
                  <span className="text-[#6C757D]">{fmtDate(a.timestamp)} · {a.user_name}</span>
                </div>
                <div className="text-xs text-[#6C757D] mt-0.5">{a.comment}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }
function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]">{children}</div>
    </div>
  );
}
function FieldEdit({ label, value, onChange, type = "text", select, options, textarea, mono }) {
  const cls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30" + (mono ? " font-mono" : "");
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {select ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
          {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls + " h-20"} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}
